/**
 * MUI `Dialog` with the close button the desk asked for on every dialog.
 *
 * WHY A WRAPPER, NOT 80 EDITS. This app has two dialog families: the ADR-061
 * `Dialog` in `@components/ui-tw` (which owns its own header and now renders an
 * X there), and MUI's `Dialog`, used by roughly eighty finance/master-data
 * screens that predate it. Those eighty each build their own `DialogTitle`, so
 * there was no single place an X could be added — and adding one by hand to
 * eighty files is eighty chances to wire the wrong handler.
 *
 * This is that single place. Call sites import it AS `Dialog`, so their JSX is
 * untouched; the only change is where `Dialog` comes from.
 *
 * THE BUTTON IS ABSOLUTELY POSITIONED over the paper rather than inserted into
 * the title, for the same reason: it must not depend on what any individual
 * dialog put in its own header, and several of them put nothing there at all.
 *
 * IT RESPECTS NON-DISMISSIBLE DIALOGS. No `onClose` means the dialog is closed
 * by its own footer actions only (a confirm step that must not be escaped), and
 * no X is drawn — an X that does nothing is worse than none. MUI's own reasons
 * are passed through unchanged, with `backdropClick` used for the button so a
 * dialog that already discriminates on reason keeps behaving as it did.
 *
 * PORTALS INTO THE NEAREST NATIVE `<dialog>`, IF ANY. `PatientHistoryDialog`
 * (and every other ADR-061 `Dialog` in `@components/ui-tw`) opens via
 * `showModal()`, which promotes the native `<dialog>` into the browser's TOP
 * LAYER — painted above the entire ordinary stacking context, a level no
 * z-index can reach. MUI's own Modal portals to `document.body` by default,
 * an ordinary div, so any MUI dialog opened from inside one of ours (e.g.
 * "OP Number Card" from the Patient History dialog) mounted and opened
 * exactly as coded, just BEHIND the outer panel — indistinguishable from the
 * click doing nothing. `combobox.tsx` hit the same top-layer gap for its
 * portalled list and fixed it the same way: resolve the nearest ancestor
 * `<dialog>` at open time and portal there instead of `document.body`.
 */
import { useRef, type JSX } from 'react';
import { Dialog as MuiDialog, IconButton, type DialogProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export function AppDialog({ children, container, ...props }: DialogProps): JSX.Element {
  const { onClose } = props;
  // Not rendered by `MuiDialog` itself (it portals elsewhere), so this stays
  // in place in the real DOM tree regardless of the dialog's open state —
  // exactly what `closest('dialog')` needs to find an enclosing native one.
  const anchorRef = useRef<HTMLSpanElement>(null);

  return (
    <>
      <span ref={anchorRef} hidden />
      <MuiDialog container={container ?? (() => anchorRef.current?.closest('dialog') ?? document.body)} {...props}>
        {onClose && (
          <IconButton
            aria-label="Close"
            onClick={(event) => onClose(event, 'backdropClick')}
            size="small"
            sx={{
              position: 'absolute',
              top: 6,
              right: 8,
              // Above the paper's own content, including a sticky DialogTitle.
              zIndex: 1,
              color: 'text.secondary',
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        {children}
      </MuiDialog>
    </>
  );
}

export default AppDialog;
